import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  StopOutlined,
  SyncOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, Progress, Row, Statistic, Table, Tag, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { post } from '../../api';

const STATUS = {
  '-1': { text: '已取消', color: 'default' },
  0: { text: '待付款', color: 'orange' },
  1: { text: '待商家采购', color: 'blue' },
  2: { text: '待平台发货', color: 'geekblue' },
  3: { text: '待收货', color: 'purple' },
  4: { text: '已收货', color: 'cyan' },
  5: { text: '已完成', color: 'green' },
  6: { text: '退款中', color: 'red' },
};

const money = (value) => Number(value || 0).toFixed(2);
const formatTime = (value) => (value ? String(value).replace('T', ' ').slice(5, 16) : '-');

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const rsp = await post('/admin/dashboard/overview');
      if (rsp.code) {
        message.error(rsp.msg || '加载失败');
        return;
      }
      setStats(rsp.data || {});
      setUpdatedAt(new Date().toLocaleTimeString('zh-CN', { hour12: false }));
    } catch (e) {
      message.error('网络异常，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const kpis = [
    {
      title: '今日订单',
      value: stats.orderToday,
      icon: <ShoppingCartOutlined />,
      color: '#1677ff',
      onClick: () => navigate('/orders'),
    },
    {
      title: '今日成交额',
      value: money(stats.gmvToday),
      prefix: '$',
      icon: <DollarOutlined />,
      color: '#52c41a',
      onClick: () => navigate('/orders'),
    },
    {
      title: '待平台发货',
      value: stats.pendingShip,
      icon: <ClockCircleOutlined />,
      color: '#fa8c16',
      onClick: () => navigate('/orders'),
    },
    {
      title: '待审核退款',
      value: stats.refunding,
      icon: <SyncOutlined />,
      color: '#ff4d4f',
      onClick: () => navigate('/orders'),
    },
  ];

  const userStoreCards = [
    { title: '用户总数', value: stats.userTotal, icon: <TeamOutlined />, color: '#1677ff' },
    { title: '今日新增用户', value: stats.userToday, icon: <UserAddOutlined />, color: '#13c2c2' },
    { title: '店铺总数', value: stats.storeTotal, icon: <ShopOutlined />, color: '#722ed1' },
    { title: '今日新增店铺', value: stats.storeToday, icon: <UserAddOutlined />, color: '#2f54eb' },
    { title: '订单总数', value: stats.orderTotal, icon: <ShoppingOutlined />, color: '#fa8c16' },
    { title: '禁用店铺', value: stats.storeDisabled, icon: <StopOutlined />, color: '#8c8c8c' },
  ];

  const reviewTotal = (stats.storePending || 0) + (stats.storeApproved || 0) + (stats.storeRejected || 0);
  const reviewRows = [
    { title: '审核中', value: stats.storePending || 0, color: '#fa8c16' },
    { title: '审核通过', value: stats.storeApproved || 0, color: '#52c41a' },
    { title: '审核拒绝', value: stats.storeRejected || 0, color: '#ff4d4f' },
  ];

  const recentColumns = [
    { title: '订单号', dataIndex: 'orderNo', width: 195 },
    { title: '店铺', dataIndex: 'storeName', ellipsis: true, render: (value) => value || '-' },
    { title: '金额', dataIndex: 'totalAmount', width: 90, render: (value) => `$${money(value)}` },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value) => {
        const item = STATUS[value] || { text: value, color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    { title: '下单时间', dataIndex: 'createTime', width: 100, render: formatTime },
  ];

  return (
    <>
      <div className="list-toolbar">
        <div className="list-title">数据概览</div>
        <div className="list-actions">
          {updatedAt ? <span className="dash-updated">更新于 {updatedAt}</span> : null}
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchStats}>
            刷新
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        {kpis.map((card) => (
          <Col xs={24} sm={12} xl={6} key={card.title}>
            <Card hoverable loading={loading} onClick={card.onClick}>
              <Statistic
                title={card.title}
                value={card.value ?? 0}
                prefix={
                  <>
                    <span style={{ color: card.color, marginRight: 4 }}>{card.icon}</span>
                    {card.prefix}
                  </>
                }
                valueStyle={{ color: card.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={16}>
          <Card
            title="最近订单"
            size="small"
            extra={<Button type="link" size="small" onClick={() => navigate('/orders')}>查看全部</Button>}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              rowKey="id"
              size="small"
              loading={loading}
              columns={recentColumns}
              dataSource={stats.recentOrders || []}
              pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card
            title="店铺审核"
            size="small"
            extra={<Button type="link" size="small" onClick={() => navigate('/stores')}>去处理</Button>}
            style={{ height: '100%' }}
          >
            {reviewRows.map((row) => (
              <div key={row.title} className="dash-review-row">
                <span>{row.title}</span>
                <Progress
                  percent={reviewTotal ? Math.round((row.value / reviewTotal) * 100) : 0}
                  size="small"
                  strokeColor={row.color}
                  format={() => row.value}
                />
              </div>
            ))}
            <div className="dash-review-tip">
              <CheckCircleOutlined style={{ color: '#52c41a' }} /> 通过率{' '}
              {reviewTotal ? Math.round(((stats.storeApproved || 0) / reviewTotal) * 100) : 0}%
              <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 16 }} /> 拒绝{' '}
              {stats.storeRejected || 0}
            </div>
            <div className="dash-quick">
              <div className="dash-quick-title">快捷入口</div>
              <Row gutter={8}>
                <Col span={8}>
                  <Button size="small" block onClick={() => navigate('/stores')}>
                    店铺管理
                  </Button>
                </Col>
                <Col span={8}>
                  <Button size="small" block onClick={() => navigate('/users')}>
                    用户管理
                  </Button>
                </Col>
                <Col span={8}>
                  <Button size="small" block onClick={() => navigate('/products')}>
                    系统商品
                  </Button>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="dash-section-title">用户与店铺</div>
      <Row gutter={[16, 16]}>
        {userStoreCards.map((card) => (
          <Col xs={24} sm={12} xl={8} key={card.title}>
            <Card loading={loading}>
              <Statistic
                title={card.title}
                value={card.value ?? 0}
                prefix={<span style={{ color: card.color }}>{card.icon}</span>}
              />
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
}
